import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import starsActions from '../../store/stars/actions'
import { clustersFilteredSelector, selectedClustersIdsSelector } from '../../store/stars/selectors'
import { breakpoints, breakpointsNoUnit, palette, pxToRem, spacing } from '../../style/theme'
import { PlpLayerWrapper } from '../CommonComponents'
import SearchIcon from '../icons/SearchIcon'
import Checkbox, { CheckBoxLabel } from '../../styleguide/Checkbox'
import { isTouchDevice } from '../../libs/url'

const Wrapper = styled(PlpLayerWrapper)`
  padding-top: ${pxToRem(spacing(2))}rem;
  padding-bottom: ${pxToRem(spacing(2))}rem;
  max-height: ${pxToRem(600)}rem;
  min-width: 24em;
  max-width: 40em;
  width: max-content;
  top: auto;

  @media (max-width: ${breakpoints.M}) {
    min-width: 27em;
    max-width: 50em;

    @media (orientation: portrait) {
      max-width: 40em;
    }
  }

  @media (min-width: ${breakpointsNoUnit.M + 1}px) {
    bottom: 1em;
    transform: translate(100%, 50%);
  }
`

const InputWrapper = styled.div`
  padding: ${pxToRem(spacing(0.5))}rem ${pxToRem(spacing(2))}rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${palette.black};
  border-radius: ${pxToRem(spacing(1))}rem;
  border: solid 1px ${palette.alto};
`

const TextInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
`

const IconSearch = styled(SearchIcon)`
  width: ${pxToRem(spacing(3))}rem;
  height: ${pxToRem(spacing(3))}rem;
`

const UlMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const LiMenu = styled.li`
  display: flex;
  align-items: center;
  gap: ${pxToRem(spacing(1.25))}rem;
  padding-top: ${pxToRem(spacing(2.5))}rem;
  padding-bottom: ${pxToRem(spacing(2.5))}rem;
  border-bottom: 1px solid ${palette.alto};
  width: 100%;
  overflow: hidden;

  label {
    width: 100%;

    > span {
      overflow: hidden;

      .input-custom {
        aspect-ratio: 1;
      }

      ${CheckBoxLabel} {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .ant-checkbox-inner {
    background: ${palette.selago};
    border: 0.025rem solid ${palette.cornflowerBlue};
  }
  .ant-checkbox-input:checked + .ant-checkbox-inner {
    background: ${palette.congressBlue};
  }
`

const LiTextWrapper = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  padding-right: 20px;
`

const LiText = styled.span<{ $isTouch: boolean }>`
  font-size: 0.875rem;
  line-height: 1.3rem;
  color: ${palette.biscay};
  text-transform: uppercase;
  display: block;
  overflow-x: ${({ $isTouch }) => ($isTouch ? 'auto' : 'hidden')};
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  text-overflow: ${({ $isTouch }) => ($isTouch ? 'unset' : 'ellipsis')};

  &::-webkit-scrollbar {
    display: none;
  }
`

const FadeOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  &::before {
    content: '...';
    position: absolute;
    right: 7px;
    top: 0.35rem;
    font-size: 0.875rem;
    color: ${palette.biscay};
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${pxToRem(70)}rem;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.75));
  }
`

const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${pxToRem(spacing(5))}rem;
  margin-top: ${pxToRem(spacing(3))}rem;
`

const MenuButton = styled.button`
  width: 100%;
  border-radius: 3rem;
  border: solid 1px ${palette.congressBlue};
  background-color: ${palette.white};
  color: ${palette.congressBlue};
  text-transform: uppercase;
  font-size: 0.875rem;
  text-align: center;

  &.clickable {
    background-color: ${palette.congressBlue};
    color: ${palette.white};
  }
`

type ClusterItemProps = {
  description: string
  checked: boolean
  onChange: () => void
}

const ClusterItem: React.FC<ClusterItemProps> = ({ description, checked, onChange }) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const [showFade, setShowFade] = useState(false)
  const isTouch = isTouchDevice()

  useEffect(() => {
    const element = textRef.current
    if (element) {
      const hasOverflow = element.scrollWidth > element.clientWidth
      setShowFade(hasOverflow)
    }
  }, [description])

  const handleScroll = (e: React.UIEvent<HTMLSpanElement>) => {
    if (!isTouch) return
    const element = e.currentTarget
    const isAtEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1
    setShowFade(!isAtEnd)
  }

  return (
    <LiMenu>
      <Checkbox checked={checked} onChange={onChange}>
        <LiTextWrapper>
          <LiText ref={textRef} onScroll={handleScroll} title={description} $isTouch={isTouch}>
            {description}
          </LiText>
          {isTouch && <FadeOverlay $visible={showFade} />}
        </LiTextWrapper>
      </Checkbox>
    </LiMenu>
  )
}

type Props = {
  open: boolean
}

const ClusterMenu: React.FC<Props> = ({ open }) => {
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')

  const clusters = useSelector(clustersFilteredSelector)
  const selectedClustersIds = useSelector(selectedClustersIdsSelector)

  const dispatch = useDispatch()

  return !open ? null : (
    <Wrapper scrollbarColor={palette.alto}>
      <InputWrapper>
        <TextInput
          placeholder="Search"
          onChange={e => setSearchTerm(e.currentTarget.value.toLowerCase())}
        />
        <IconSearch />
      </InputWrapper>
      <UlMenu>
        {clusters
          .filter(({ description }) => {
            return searchTerm ? description.toLowerCase().includes(searchTerm) : true
          })
          .sort((a, b) => (a.id > b.id ? 1 : -1))
          .map(({ id, description }) => (
            <ClusterItem
              key={id}
              description={description}
              checked={selectedClustersIds.includes(id)}
              onChange={() =>
                dispatch(
                  starsActions.setSelectedClustersIds(
                    selectedClustersIds.includes(id)
                      ? selectedClustersIds.filter(selectedId => selectedId !== id)
                      : selectedClustersIds.concat(id),
                  ),
                )
              }
            />
          ))}
      </UlMenu>
      <ButtonsWrapper>
        <MenuButton
          className={selectedClustersIds.length === clusters.length ? 'clickable' : ''}
          onClick={() => dispatch(starsActions.setSelectedClustersIds([]))}
        >
          {t('FooterNav.cart_deselectall')}
        </MenuButton>
        <MenuButton
          className={selectedClustersIds.length < clusters.length ? 'clickable' : ''}
          onClick={() =>
            dispatch(starsActions.setSelectedClustersIds(clusters.map(({ id }) => id)))
          }
        >
          {t('FooterNav.cart_selectall')}
        </MenuButton>
      </ButtonsWrapper>
    </Wrapper>
  )
}

export default ClusterMenu
